import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { currencyMapper } from '@shared/utils/src/ct/index';
import type { AddToCartInput, Cart } from '@storefront/lib-global/codegen/types';
import { useCartContext } from '@storefront/lib-global/context/cartContext';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import { useAddToBag } from '@storefront/lib-global/hooks/useAddToBag';
import { sendAddToCartFromQuickBuyEvent } from '@storefront/lib-global/utils/gtm/events/add_to_cart/sendAddToCartFromQuickBuyEvent';
import { useSizeAndWidthContext } from '../../../context';
import { useStockAvailabilityInfo } from '../../../hooks';
import { useCreateCart } from '../../../hooks/useCreateCart';
import { MeasurementList } from '../MeasurementBlock/MeasurementBlock';
import {
  SizeAndWidthSelectorContainer,
  SizeHeadText,
  SizeSelectorContainer,
} from '../SizeAndWidthSelectors/SizeAndWithSelectors.styles';
import { Sizes } from '../Sizes/Sizes';
import { WidthSelector } from '../WidthSelector/WidthSelector';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { StockAvailabilityInfo } from '../stockAvailabilityInfo/StockAvailabilityInfo';
import { AddToBagButton } from './QuickAddToBag.styles';

interface QuickAddToBagSizeAndWidthSelectorsProps {
  onClose?: () => void;
  productName?: string;
}

export function QuickAddToBagSizeAndWidthSelectors({ onClose, productName }: QuickAddToBagSizeAndWidthSelectorsProps) {
  const { t } = useTranslation('lib-products-common');
  const { uniqueSizes, error, selectedSku, setError, selectedSize, selectedWidth } = useSizeAndWidthContext();
  const availabilityInfo = useStockAvailabilityInfo();
  const isOneSizeProduct = uniqueSizes.length === 1;
  const [isLoading, setIsLoading] = useState(false);
  const [addToBagError, setAddToBagError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { locale } = useLocaleContext();
  const { anonymousId, cartId } = useCartContext();
  const { baseUrl } = useStaticContext();
  const { addToBag } = useAddToBag();
  const { createCart } = useCreateCart();

  const handleOnCompleted = (sku: string, addToCart: Cart) => {
    setIsLoading(false);
    setAddToBagError(false);
    setSuccessMessage(t('quick.add.product.to.basket.success', { size: selectedSize, width: selectedWidth }));
    sendAddToCartFromQuickBuyEvent(sku, addToCart, baseUrl);
    onClose?.();
  };

  const handleAddToBag = async () => {
    const missingFields: string[] = [];
    if (!selectedSize) missingFields.push('size');
    if (!selectedWidth) missingFields.push('width');

    if (!selectedSku) {
      setError(missingFields);
      return;
    }

    const input: AddToCartInput = {
      currency: currencyMapper[locale],
      locale,
      sku: selectedSku,
      quantity: 1,
      cartId,
      ...(anonymousId && { anonymousId }),
    };

    setIsLoading(true);
    setAddToBagError(false);

    try {
      if (cartId) {
        await addToBag({
          variables: { input },
          onCompleted: ({ addToCart }) => handleOnCompleted(input.sku, addToCart as Cart),
          onError: () => {
            setIsLoading(false);
            setAddToBagError(true);
          },
        });
        return;
      }

      await createCart({
        onCompleted: async ({ createCart: createCartData }) => {
          const { id: createCartId, anonymousId: createCartAnonId } = { ...createCartData };

          await addToBag({
            variables: {
              input: {
                ...input,
                cartId: createCartId,
                anonymousId: createCartAnonId,
              },
            },
            onCompleted: ({ addToCart }) => handleOnCompleted(input.sku, addToCart as Cart),
            onError: () => {
              setIsLoading(false);
              setAddToBagError(true);
            },
          });
        },
        onError: () => {
          setIsLoading(false);
          setAddToBagError(true);
        },
      });
    } catch (e) {
      setIsLoading(false);
      setAddToBagError(true);
    }
  };

  return (
    <SizeAndWidthSelectorContainer>
      {uniqueSizes?.length > 0 && (
        <SizeSelectorContainer>
          <SizeHeadText>{t('size.selector.select.your.size')}:</SizeHeadText>
          <MeasurementList isOneSizeProduct={isOneSizeProduct}>
            <Sizes />
          </MeasurementList>
          {error.includes('size') ? (
            <ErrorMessage testId="noSizeSelectedError">{t('size.selector.error.message')}</ErrorMessage>
          ) : null}
        </SizeSelectorContainer>
      )}
      <WidthSelector />

      <StockAvailabilityInfo
        message={availabilityInfo?.message || null}
        testId={availabilityInfo?.testId || null}
        size={availabilityInfo?.size || null}
        width={availabilityInfo?.width || null}
      />
      <AddToBagButton
        type="button"
        data-testid="quickAddToBagButton"
        aria-label={`${t('quick.add.product.to.basket')}${productName ? ` - ${productName}` : ''}`}
        onClick={handleAddToBag}
        disabled={isLoading || ((!selectedSize || !selectedWidth) && !isOneSizeProduct)}
      >
        {t('pdp.add.to.bag')}
      </AddToBagButton>
      {addToBagError && <ErrorMessage role="alert">{t('pdp.add.to.bag.error')}</ErrorMessage>}
      <span aria-live="polite" className="visually-hidden">
        {successMessage}
      </span>
    </SizeAndWidthSelectorContainer>
  );
}

export default QuickAddToBagSizeAndWidthSelectors;
